import React, { useState, FC } from 'react';
import cn from 'clsx';
import { css } from 'linaria';
import { DialogOverlay, DialogContent } from '@reach/dialog';

import useModalToggleTracking from '../../hooks/useModalToggleTracking';

export interface MobileDrawerProps {
  className?: string;
  children: any;
  isOpen: boolean;
  direction: 'left' | 'right' | 'bottom';
  toggleCallback: any;
  ariaLabel: string;
  tracking: any;
}

const HorizontalDrawerMaxSizing = css`
  width: 85%;
  max-width: 22.5rem;
`;

const VerticalDrawerMaxSizing = css`
  width: 100%;
`;

const SlideInLeftStyles = css`
  animation: slide-in-left 0.3s forwards cubic-bezier(0.18, 0.96, 0.53, 0.97);

  @keyframes slide-in-left {
    0% {
      transform: translate3d(-101%, 0, 0);
    }

    100% {
      transform: translate3d(0, 0, 0);
    }
  }
`;
const SlideInRightStyles = css`
  animation: slide-in-right 0.3s forwards cubic-bezier(0.18, 0.96, 0.53, 0.97);

  @keyframes slide-in-right {
    0% {
      transform: translate3d(101%, 0, 0);
    }

    100% {
      transform: translate3d(0, 0, 0);
    }
  }
`;
const SlideInBottomStyles = css`
  animation: slide-in-bottom 0.3s forwards cubic-bezier(0.18, 0.96, 0.53, 0.97);

  @keyframes slide-in-bottom {
    0% {
      transform: translate3d(0, 101%, 0);
    }

    100% {
      transform: translate3d(0, 0, 0);
    }
  }
`;
const FadeInStyles = css`
  animation: fade-in 0.3s forwards cubic-bezier(0.18, 0.96, 0.53, 0.97);

  @keyframes fade-in {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }
`;
const BoxShadow = css`
  box-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.5);
`;
const ReverseAnimation = css`
  animation-direction: reverse !important;
`;

const MobileDrawer: FC<MobileDrawerProps> = (props: MobileDrawerProps) => {
  const { ariaLabel, children, className, direction, isOpen, toggleCallback, tracking } = props;
  useModalToggleTracking(isOpen, tracking);
  const [animationStatus, setAnimationStatus] = useState('closed');
  const animationEnded = () => {
    if (animationStatus === 'closing') {
      setAnimationStatus('closed');
    } else if (animationStatus === 'opening') {
      setAnimationStatus('open');
    }
  };

  let currentAnimationStatus = animationStatus;
  if (isOpen && animationStatus.indexOf('open') === -1) {
    currentAnimationStatus = 'opening';
  } else if (!isOpen && animationStatus.indexOf('clos') === -1) {
    currentAnimationStatus = 'closing';
  }
  if (currentAnimationStatus !== animationStatus) {
    setAnimationStatus(currentAnimationStatus);
  }

  const MaxSizingClass =
    direction === 'bottom' ? VerticalDrawerMaxSizing : HorizontalDrawerMaxSizing;
  const ReverseClass =
    isOpen === false && animationStatus !== 'closed' ? ReverseAnimation : undefined;
  const isAnimating = currentAnimationStatus.indexOf('ing') > -1;
  const dialogClassName = cn(
    'fixed top-0 bottom-0 left-0 right-0 overflow-y-hidden overflow-x-hidden'
  );
  const curtainClassName = cn(
    dialogClassName,
    FadeInStyles,
    'bg-lapel bg-opacity-20 fade-in pointer-events-none',
    ReverseClass
  );
  const drawerClassName = cn(
    className,
    MaxSizingClass,
    isAnimating && direction === 'left' ? SlideInLeftStyles : undefined,
    isAnimating && direction === 'right' ? SlideInRightStyles : undefined,
    isAnimating && direction === 'bottom' ? SlideInBottomStyles : undefined,
    'fixed overflow-y-scroll bg-white',
    {
      'h-full': direction === 'left' || direction === 'right',
      'left-0': direction === 'left',
      'right-0': direction === 'right',
      'bottom-0': direction === 'bottom',
      '-left-full': !isOpen && currentAnimationStatus === 'closed' && direction === 'left',
      '-right-full': !isOpen && currentAnimationStatus === 'closed' && direction === 'right',
      '-bottom-full': !isOpen && currentAnimationStatus === 'closed' && direction === 'bottom',
    },
    ReverseClass,
    BoxShadow
  );

  const isModalOpen = animationStatus !== 'closed';

  return (
    <DialogOverlay
      className={cn('z-20', dialogClassName)}
      isOpen={isModalOpen}
      onAnimationEnd={animationEnded}
      onDismiss={toggleCallback}
    >
      <div className={curtainClassName}></div>
      <DialogContent aria-label={ariaLabel} className={drawerClassName}>
        {children}
      </DialogContent>
    </DialogOverlay>
  );
};

export default MobileDrawer;
